import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getFullUrl } from '../config';

function EventDetailsView({ event, onBack, onRegister }) {
  const { t } = useTranslation();
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isEventUpcoming = (startDate) => {
    return new Date(startDate) > new Date();
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    
    if (!registrationData.name.trim() || !registrationData.email.trim() || !registrationData.phone.trim()) {
      setError(t('fillRequiredFields'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registrationData.email)) {
      setError(t('validEmailRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/event-registrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...registrationData,
          occurrenceId: event.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to register for event');
      }

      setSuccess(true);
      if (onRegister) onRegister(event);
      
    } catch (err) {
      console.error('Failed to register for event:', err);
      setError(t('registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({ ...prev, [name]: value }));
  };

  if (success) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center py-8 max-w-md mx-auto">
          <div className="text-green-600 mb-4 text-lg sm:text-xl">✓ {t('registrationSuccessful')}</div>
          <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">{t('contactSoon').replace('information', `details about "${event.title}"`)}</p>
          <button
            onClick={onBack}
            className="w-full sm:w-auto px-6 py-3 sm:px-4 sm:py-2 bg-gray-900 text-white rounded text-sm hover:bg-black transition-colors"
          >
            {t('backToEvents')}
          </button>
        </div>
      </div>
    );
  }

  if (showRegistration) {
    return (
      <div className="p-4 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">{t('registerForEventTitle')}</h3>
          <p className="text-sm text-gray-600">"{event.title}" - {formatDate(event.startDate)}</p>
        </div>

        <form onSubmit={handleRegistrationSubmit} className="max-w-md mx-auto sm:max-w-none">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} *</label>
              <input
                type="text"
                name="name"
                value={registrationData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')} *</label>
              <input
                type="email"
                name="email"
                value={registrationData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')} *</label>
              <input
                type="tel"
                name="phone"
                value={registrationData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('message')}</label>
              <textarea
                name="message"
                value={registrationData.message}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500 resize-none"
                placeholder={t('anyQuestions')}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded">{error}</div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowRegistration(false)}
                className="w-full sm:flex-1 px-4 py-3 sm:py-2 text-gray-700 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:flex-1 px-4 py-3 sm:py-2 bg-gray-900 text-white rounded text-sm hover:bg-black transition-colors disabled:opacity-50"
              >
                {loading ? t('submitting') : t('submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Mobile-friendly responsive layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        {/* Image Section */}
        <div>
          {event.imageUrl ? (
            <img 
              src={getFullUrl(event.imageUrl)} 
              alt={event.title}
              className="w-full h-48 sm:h-64 lg:h-[400px] object-cover bg-gray-50 rounded border"
              onError={(e) => {
                e.target.style.display = 'none';
                console.warn('Failed to load event image:', event.imageUrl);
              }}
            />
          ) : (
            <div className="w-full h-48 sm:h-64 lg:h-[400px] bg-gray-100 rounded border flex items-center justify-center">
              <div className="text-gray-400 text-2xl sm:text-4xl">📅</div>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="space-y-4 sm:space-y-6">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4">
              <h3 className="text-xl sm:text-2xl font-medium text-gray-900 mb-2 sm:mb-0 pr-2">{event.title}</h3>
              <span className="px-3 py-1 text-xs sm:text-sm bg-gray-100 text-gray-600 rounded self-start">
                {event.type === 'online' ? t('online') : t('inPerson')}
              </span>
            </div>
            
            <div className="text-xs sm:text-sm text-gray-500 mb-4 space-y-1">
              <div><strong>{t('date')}:</strong> {formatDate(event.startDate)}</div>
              {event.endDate && <div><strong>{t('ends')}:</strong> {formatDate(event.endDate)}</div>}
              {event.location && <div><strong>{t('location')}:</strong> {event.location}</div>}
              {event.price && <div><strong>Цена:</strong> <span className="font-medium text-gray-700">{event.price} ₽</span></div>}
            </div>

            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{event.description}</p>
          </div>

          {/* Event Details */}
          {event.maxAttendees && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">{t('eventDetails')}</h4>
              <div className="space-y-2">
                <div className="p-3 bg-gray-50 rounded border">
                  <div className="text-xs sm:text-sm text-gray-500">{t('availableSpaces')}</div>
                  <div className="font-medium text-gray-900 text-sm sm:text-base">
                    {event.maxAttendees - (event.registrations?.length || 0)} {t('of')} {event.maxAttendees}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {event.registrationEnabled && isEventUpcoming(event.startDate) && (
            <div className="pt-2">
              <button
                onClick={() => setShowRegistration(true)}
                className="w-full inline-flex items-center justify-center px-4 py-3 bg-gray-900 text-white font-medium rounded hover:bg-black transition-colors text-sm sm:text-base"
              >
                {t('registerForEvent')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventDetailsView;